import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FacadeService } from 'src/app/core/services/facade.service';
import { Beneficiaire } from 'src/app/shared/models/beneficiaire';
import { ImportResponse } from 'src/app/shared/models/api-response/import-response';
import { User } from 'src/app/shared/models/security/user';
import { saveAs } from 'file-saver';
import { PARAM_LABEL_BENEFICIAIRE } from 'src/app/core/services/constant/constants.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BeneficiaireModalAjoutFormComponent } from '../modal/beneficiaire-modal-ajout-form/beneficiaire-modal-ajout-form.component';
import { ErrorResponse } from 'src/app/shared/models/api-response/error-response';
import { Subject, debounceTime, take } from 'rxjs';
import { LogService } from 'src/app/core/services/log/log.service';
import { ListingResponse } from 'src/app/shared/models/listing/listing-response';
import { CareSkill } from 'src/app/shared/models/employee/care-skill';
import { CenterDepartment } from 'src/app/shared/models/center-department';

export type SortOrder = 'asc' | 'desc';

@Component({
	selector: 'app-list-beneficiaire',
	templateUrl: './list-beneficiaire.component.html',
	styleUrls: ['./list-beneficiaire.component.scss']
})
export class ListBeneficiaireComponent implements OnInit {
	beneficiaires: Beneficiaire[];
	currentPage = 1;

	beneficiaireCsvFile: File = null;
	sortType = 'lastName';
	sortOrder: SortOrder = 'asc';

	nextWeekNumber: number;
	competences: CareSkill[];

	// Liste des compétences sélectionnées dans le filtre
	selectedCompetences: string[] = [];
	// Liste des compétences validées après sélection, celles par lesquelles le listing est filtré
	competencesToFilterBy: string = '';

	searchedInput = '';

	// nombre de page totale pour la pagination
	totalItems: number = 0;
	itemsPerPage = 10;

	user: User = null;
	isPlanif = false;
	labelBeneficiaireCapitalized: string;
	labelBeneficiairePluralized: string;

	ajoutBeneficiaireModalref: BsModalRef;

	isAddable = false;
	isImportable = false;
	isExportable = false;

	isMobile = false;
	screenWidth: number;

	isSESSAD = false;
	isSAAD = false;
	isSSIAD = false;
	isSAE = false;

	isAreaActive: boolean;

	isListingLoading: boolean = true;

	centerDepartments: CenterDepartment[] = [];
	selectedDepartments: CenterDepartment[] = [];

	private searchSubject = new Subject<string>();

	constructor(
		private router: Router,
		private facade: FacadeService,
		private modalService: BsModalService,
		private logger: LogService
	) {
		// ajout de délai à la saisie de recherche pour ne pas bombarder le back de requête
		this.searchSubject
			.pipe(
				debounceTime(300) // Délai en millisecondes
			)
			.subscribe(() => {
				this.searchByInput();
			});
	}

	@HostListener('window:resize', ['$event'])
	onWindowResize() {
		this.screenWidth = window.innerWidth;
		if (this.screenWidth < 1200) {
			this.isMobile = true;
		} else {
			this.isMobile = false;
		}
	}

	ngOnInit(): void {
		this.screenWidth = window.innerWidth;
		this.isMobile =
			this.facade.mobileService.isMobile() || this.screenWidth < 1200;

		this.labelBeneficiaireCapitalized =
			this.facade.dataStorageService.getLabelCapitalized(
				PARAM_LABEL_BENEFICIAIRE
			);
		this.labelBeneficiairePluralized =
			this.facade.dataStorageService.getLabelPluralized(
				PARAM_LABEL_BENEFICIAIRE
			);

		this.user = this.facade.dataStorageService.getUser();
		this.isPlanif = this.facade.dataStorageService.isPlanificatrice();

		this.isSESSAD = this.facade.dataStorageService.isSESSAD();
		this.isSAAD = this.facade.dataStorageService.isSAAD();
		this.isSSIAD = this.facade.dataStorageService.isSSIAD();
		this.isSAE = this.facade.dataStorageService.isSAE();

		this.isAreaActive = this.facade.dataStorageService.isAreaActive();

		this.facade.careSkillService
			.getAllCareSkills()
			.subscribe((careskills) => (this.competences = careskills));

		if (this.isPlanif) {
			this.isExportable = true;
			this.isAddable = true;
			this.isImportable = true;
		}

		this.getBeneficiairesMinimal();
	}

	getBeneficiairesMinimal(): void {
		this.isListingLoading = true;
		this.facade.beneficiaireService
			.getBeneficiairesPageable(
				this.currentPage - 1, // -1 car la pagination commence à 0 côté back
				this.itemsPerPage,
				this.searchedInput.toLowerCase(),
				this.competencesToFilterBy
			)
			.subscribe({
				next: (beneficiaireListing: ListingResponse) => {
					this.beneficiaires = beneficiaireListing.dataList;
					this.totalItems = beneficiaireListing.totalItems;
					this.centerDepartments = this.getBeneficiairesDepartments(
						this.beneficiaires
					);
				},
				error: (err: ErrorResponse) => {
					this.logger.error(err + '');
				},
				complete: () => {
					this.isListingLoading = false;
				}
			});
	}

	selectBeneficiaire(beneficiaire: Beneficiaire): void {
		const link = ['/gestion/beneficiaires', beneficiaire.id];
		this.router.navigate(link);
	}

	getBeneficiairesDepartments(
		beneficiaires: Beneficiaire[]
	): CenterDepartment[] {
		return beneficiaires
			.flatMap((beneficiaire) => beneficiaire.centerDepartments)
			.filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i)
			.sort((a, b) => a.name.localeCompare(b.name));
	}

	importBeneficiaires(event: any): void {
		this.isImportable = false;
		this.beneficiaireCsvFile = event.target.files[0];
		this.facade.beneficiaireService
			.importBeneficiaires(this.beneficiaireCsvFile)
			.subscribe({
				next: (importStats: ImportResponse) => {
					this.facade.toastService.createSuccessToast('Import effectué');
					this.getBeneficiairesMinimal();
					this.isImportable = true;
				},
				error: (err: ErrorResponse) => {
					this.isImportable = true;
					this.facade.toastService.createHttpErrorToast(
						err.error.response ? err.error.response : '',
						err.status
					);
				}
			});
	}

	importFromOcashe() {
		this.isListingLoading = true; // Si tu as un loader
		this.facade.beneficiaireService.importBeneficiairesFromOcashe().subscribe({
			next: (result) => {
				this.getBeneficiairesMinimal(); // À adapter avec ta méthode de reload
			},
			error: (err) => {
				this.logger.error(err);
			},
			complete: () => {
				// this.isLoading = false;
			}
		});
	}

	exportBeneficiaires(): void {
		this.facade.exportService
			.exportBeneficiaires()
			.subscribe((blob) => saveAs(blob, 'beneficiaire.csv'));
	}

	/**
	 * Fonction pour les paramètres de trie. Si le string choisi vaut le string courant l'on change l'ordre de trie (croissant/décroissant)
	 * sinon l'on chance la colonne de trie et l'on trie par ordre crosisant
	 * @param str colonne de trie
	 */
	sortChange(str: string): void {
		if (str === this.sortType) {
			if (this.sortOrder === 'asc') {
				this.sortOrder = 'desc';
			} else {
				this.sortOrder = 'asc';
			}
		} else {
			this.sortType = str;
			this.sortOrder = 'asc';
		}
	}

	onKeyUp() {
		this.searchSubject.next(this.searchedInput);
	}

	/**
	 * Ajoute ou supprime la compétence à la liste des compétences sélectionnées et mets à jour la chaîne de caractères affichée.
	 * @param values Element html
	 * @param competence String representant la compétence choisie
	 */
	changeCompetencesFilter(values: any, competence: string): void {
		// Add
		if (values.currentTarget.checked) {
			const clone = [...this.selectedCompetences];
			clone.push(competence);
			this.selectedCompetences = clone;
		} else {
			// Remove
			this.selectedCompetences = this.selectedCompetences.filter(
				(e) => e !== competence
			);
		}
	}

	changeDepartmentFilter(values: any, department: CenterDepartment): void {
		// Add
		if (values.currentTarget.checked) {
			const clone = [...this.selectedDepartments];
			clone.push(department);
			this.selectedDepartments = clone;
		} else {
			// Remove
			this.selectedDepartments = this.selectedDepartments.filter(
				(e) => e !== department
			);
		}
	}

	filterListByCompetences() {
		this.competencesToFilterBy = this.selectedCompetences.join(',');
		this.currentPage = 1;
		this.getBeneficiairesMinimal();
	}

	searchByInput() {
		this.currentPage = 1;
		this.getBeneficiairesMinimal();
	}

	openModalAjout() {
		const initialState = {
			serviceCenter: this.user.employee.serviceCenter
		};
		if (this.user === null || this.user.employee.serviceCenter === null) {
			this.facade.toastService.createErrorToast(
				"Vous n'êtes pas rattaché à un centre."
			);
		} else {
			this.ajoutBeneficiaireModalref = this.modalService.show(
				BeneficiaireModalAjoutFormComponent,
				{
					class: 'modal-lg',
					initialState,
					backdrop: 'static'
				}
			);
			this.ajoutBeneficiaireModalref.content.updateListing
				.pipe(take(1))
				.subscribe((addedBeneficiaire: Beneficiaire) => {
					this.beneficiaires.push(addedBeneficiaire);
				});
		}
	}

	pageHasChanged(pageNumber: any) {
		this.currentPage = pageNumber;

		this.getBeneficiairesMinimal();
	}
}
